import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import TopicPage from "@/pages/TopicPage";
import RunsPage from "@/pages/RunsPage";
import SourcesPage from "@/pages/SourcesPage";
import RetentionPage from "@/pages/RetentionPage";
import InnovationPage from "@/pages/InnovationPage";
import BotPage from "@/pages/BotPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import AccountPage from "@/pages/AccountPage";
import OwnerPage from "@/pages/OwnerPage";
import MyLibraryPage from "@/pages/MyLibraryPage";
import ReadLaterPage from "@/pages/ReadLaterPage";
import RankingPage from "@/pages/RankingPage";
import FunFactsPage from "@/pages/FunFactsPage";
import HealthPage from "@/pages/HealthPage";
import PricingPage from "@/pages/PricingPage";
import LegalPage from "@/pages/LegalPage";
import WatchPage from "@/pages/WatchPage";
import CollabPage from "@/pages/CollabPage";
import CollabAcceptPage from "@/pages/CollabAcceptPage";
import WhitelistApprovePage from "@/pages/WhitelistApprovePage";
import SecurityPage from "@/pages/SecurityPage";
import WallPage from "@/pages/WallPage";
import TeamsPage from "@/pages/TeamsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelPage from "@/pages/BillingCancelPage";
import OneStampPage from "@/pages/OneStampPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import OneStampPublicVerifyPage from "@/pages/OneStampPublicVerifyPage";
import EmailPage from "@/pages/EmailPage";
import LiveResultsPage from "@/pages/LiveResultsPage";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";

function RouterShell() {
  const loc = useLocation();
  // Synchronous fragment check (Emergent Google Auth) BEFORE rendering routes.
  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/live-results" element={<LiveResultsPage />} />      <Route path="/owner" element={<OwnerPage />} />
      <Route path="/collab/accept" element={<CollabAcceptPage />} />
      <Route path="/whitelist/approve" element={<WhitelistApprovePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/billing/success" element={<BillingSuccessPage />} />
      <Route path="/billing/cancel" element={<BillingCancelPage />} />
      <Route
        path="/how-it-works"
        element={
          <ProtectedRoute>
            <Layout><HowItWorksPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onestamp"
        element={
          <ProtectedRoute>
            <Layout><OneStampPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/onestamp/verify/:code" element={<OneStampPublicVerifyPage />} />
      <Route
        path="/email"
        element={
          <ProtectedRoute>
            <Layout><EmailPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/topics/:slug"
        element={
          <ProtectedRoute>
            <Layout><TopicPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/runs"
        element={
          <ProtectedRoute>
            <Layout><RunsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sources"
        element={
          <ProtectedRoute>
            <Layout><SourcesPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/retention"
        element={
          <ProtectedRoute>
            <Layout><RetentionPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/innovation"
        element={
          <ProtectedRoute requireFeature="innovation">
            <Layout><InnovationPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bot"
        element={
          <ProtectedRoute requireFeature="bot">
            <Layout><BotPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Layout><AccountPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Layout><MyLibraryPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/read-later"
        element={
          <ProtectedRoute>
            <Layout><ReadLaterPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ranking"
        element={
          <ProtectedRoute>
            <Layout><RankingPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/facts"
        element={
          <ProtectedRoute>
            <Layout><FunFactsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/watch"
        element={
          <ProtectedRoute>
            <Layout><WatchPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collab"
        element={
          <ProtectedRoute>
            <Layout><CollabPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <Layout><SecurityPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wall"
        element={
          <ProtectedRoute>
            <Layout><WallPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <Layout><TeamsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/health"
        element={
          <ProtectedRoute>
            <Layout><HealthPage /></Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouterShell />
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "#0A0A0A",
              border: "1px solid #27272A",
              borderRadius: 0,
              color: "#fff",
              fontFamily: "IBM Plex Mono, monospace",
              fontSize: "11px",
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
